import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";

import { createApiClient } from "@/config/createApiClient";
import { selectedClientServiceIdAtom } from "@/store";

import { OrderItemProofApi } from "../../config/apiClient";

import { useWithTrackingMutation } from "./useWithTrackingMutation";

const orderItemProofApiClient = createApiClient(OrderItemProofApi);

export const useOrderItemProofs = () => {
  const queryClient = useQueryClient();
  const selectedClientServiceIdAtomValue = useAtomValue(
    selectedClientServiceIdAtom
  );

  const useApproveOrderItemProof = () =>
    useWithTrackingMutation<
      {
        orderId: string;
        orderItemId: string;
        sandboxItemProofId: string;
        heapMetadata: { orderId: string | undefined };
      },
      Awaited<
        ReturnType<
          typeof orderItemProofApiClient.orderItemProofsControllerPatchOrderItemProof
        >
      >
    >(
      (params: {
        orderId: string;
        orderItemId: string;
        sandboxItemProofId: string;
        heapMetadata: { orderId: string | undefined };
      }) =>
        orderItemProofApiClient.orderItemProofsControllerPatchOrderItemProof({
          clientServiceId: selectedClientServiceIdAtomValue,
          sandboxItemProofId: params.sandboxItemProofId,
          orderId: params.orderId,
          orderItemId: params.orderItemId,
          orderItemProofPatchCommand: {
            isValidated: true,
            isCancelled: false,
          },
        }),
      [
        {
          name: "bat_validation",
          getMetaData: (res, params) => ({
            validation_date: res?.data?.validatedAt ?? null,
            order_id: params.heapMetadata?.orderId ?? null,
          }),
        },
      ],
      {
        onSuccess: (_, params) =>
          Promise.all([
            queryClient.invalidateQueries({ queryKey: ["orders"] }),
            queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
            queryClient.refetchQueries({
              queryKey: ["orderItem", params.orderId, params.orderItemId],
            }),
            queryClient.invalidateQueries({ queryKey: ["worklist"] }),
            queryClient.invalidateQueries({
              queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
            }),
          ]),
      }
    );

  const useRefuseOrderItemProof = () =>
    useMutation({
      mutationFn: (params: {
        orderId: string;
        orderItemId: string;
        sandboxItemProofId: string;
        sandboxItemProofRefuseCommand: Parameters<
          typeof orderItemProofApiClient.orderItemProofsControllerPatchOrderItemProof
        >[0]["orderItemProofPatchCommand"];
      }) =>
        orderItemProofApiClient.orderItemProofsControllerPatchOrderItemProof({
          orderId: params.orderId,
          orderItemId: params.orderItemId,
          orderItemProofPatchCommand: params.sandboxItemProofRefuseCommand,
          clientServiceId: selectedClientServiceIdAtomValue,
          sandboxItemProofId: params.sandboxItemProofId,
        }),
      onSuccess: (_, params) =>
        Promise.all([
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.refetchQueries({
            queryKey: ["orderItem", params.orderId, params.orderItemId],
          }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
          queryClient.invalidateQueries({
            queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
          }),
        ]),
    });

  const useCancelOrderItemProof = () =>
    useMutation({
      mutationFn: (params: {
        orderId: string;
        orderItemId: string;
        sandboxItemProofId: string;
        sandboxItemProofCancelCommand: Parameters<
          typeof orderItemProofApiClient.orderItemProofsControllerPatchOrderItemProof
        >[0]["orderItemProofPatchCommand"];
      }) =>
        orderItemProofApiClient.orderItemProofsControllerPatchOrderItemProof({
          orderId: params.orderId,
          orderItemId: params.orderItemId,
          orderItemProofPatchCommand: {
            isCancelled: true,
            cancelReason: params.sandboxItemProofCancelCommand.cancelReason,
            isValidated: true,
          },
          clientServiceId: selectedClientServiceIdAtomValue,
          sandboxItemProofId: params.sandboxItemProofId,
        }),
      onSuccess: (_, params) =>
        Promise.all([
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.refetchQueries({
            queryKey: ["orderItem", params.orderId, params.orderItemId],
          }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
          queryClient.invalidateQueries({
            queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
          }),
        ]),
    });
  return {
    useApproveOrderItemProof,
    useRefuseOrderItemProof,
    useCancelOrderItemProof,
  };
};
